// Read-only ERA detail for one patient, opened from the Match ERAs screen.
//
// The matcher's remittance pane shows only what is still to be actioned on
// this account; clicking the fund name in a claim header opens this modal
// with the same header block and claims-by-patient detail as the ERA page
// (billingERA.jsx), scoped to the clicked patient, so the user can see what
// the scheme said without leaving the matcher.

import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Modal, Row, Spinner } from "react-bootstrap";

import { getERAById } from "../../afinanceapi";
import { zar } from "../Accounts/lineAllocation";
import EraPatientGroups from "./EraPatientGroups";

// Same patient-identity rule EraPatientGroups groups by.
const patientKeyOf = (hdr) => {
  const name =
    hdr.patientName ||
    `${hdr.patientFirstName || ""} ${hdr.patientLastName || ""}`.trim() ||
    "(unknown patient)";
  return `${hdr.accountMedicalAidNumber || "noma"}|${hdr.dependantCode || ""}|${name.toLowerCase()}`;
};

function HeaderField({ label, value }) {
  return (
    <div className="d-flex justify-content-between gap-2">
      <span className="text-primary fw-semibold">{label}</span>
      <span className="text-end">{value || "-"}</span>
    </div>
  );
}

export default function EraDetailModal({
  show,
  onHide,
  eraLogId,
  schemeClaimId,
  patientName,
}) {
  const [era, setEra] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show || !eraLogId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      setEra(null);
      const data = await getERAById(eraLogId);
      if (cancelled) return;
      if (data) setEra(data);
      else setError("Could not load the ERA detail.");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [show, eraLogId]);

  // The clicked claim names the patient; show every claim in the ERA that
  // belongs to that patient. Legacy ERAs without eraClaims[] fall back to an
  // empty list and render the header only.
  const patientClaims = useMemo(() => {
    const all = Array.isArray(era?.eraClaims) ? era.eraClaims : [];
    const clicked = all.find(
      (ec) => String(ec?.eraClaim?.schemeClaimId) === String(schemeClaimId)
    );
    if (!clicked) return all;
    const key = patientKeyOf(clicked.eraClaim || {});
    return all.filter((ec) => patientKeyOf(ec?.eraClaim || {}) === key);
  }, [era, schemeClaimId]);

  const h = era?.header || {};

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      scrollable
      className="era-detail-modal"
    >
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          ERA Details{patientName ? `: ${patientName}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading ERA detail…</p>
          </div>
        )}
        {!loading && error && (
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        )}
        {!loading && !error && era && (
          <>
            <Row className="mb-3">
              <Col xs={12} md={6}>
                <HeaderField label="ERA Number" value={h.MedKreditERAnbr} />
                <HeaderField label="ERA Date" value={h.ERADate} />
                <HeaderField label="Payment Date" value={h.PaymentPaidDate} />
                <HeaderField label="Payment Ref" value={h.PaymentReference} />
              </Col>
              <Col xs={12} md={6}>
                <HeaderField
                  label="Practice"
                  value={[h.PracticeNumber, h.PracticeName]
                    .filter(Boolean)
                    .join(", ")}
                />
                <HeaderField label="Medical Aid" value={h.MedicalAidName} />
                <HeaderField
                  label="Administrator"
                  value={
                    h.MedicalAidAdmin
                      ? `${h.MedicalAidAdmin}${
                          h.MedicalAidAdminCode ? ` (${h.MedicalAidAdminCode})` : ""
                        }`
                      : ""
                  }
                />
                <HeaderField label="MA Code" value={h.MedicalAidCode} />
              </Col>
            </Row>
            <Row className="text-center posted-invoice-info mx-0 mb-3">
              <Col xs={6} md={3}>
                <span className="small fw-semibold text-primary d-block">
                  Paid (Provider)
                </span>
                <span className="fw-semibold">
                  {zar.format(h.TotalPaidProvider || 0)}
                </span>
              </Col>
              <Col xs={6} md={3}>
                <span className="small fw-semibold text-primary d-block">
                  Paid (Member)
                </span>
                <span className="fw-semibold">
                  {zar.format(h.TotalPaidMember || 0)}
                </span>
              </Col>
              <Col xs={6} md={3}>
                <span className="small fw-semibold text-primary d-block">
                  Not Paid
                </span>
                <span className="fw-semibold">
                  {zar.format(h.TotalNotPaid || 0)}
                </span>
              </Col>
              <Col xs={6} md={3}>
                <span className="small fw-semibold text-primary d-block">
                  Member Liability
                </span>
                <span className="fw-semibold">
                  {zar.format(h.TotalMemberLiability || 0)}
                </span>
              </Col>
            </Row>
            {patientClaims.length > 0 ? (
              <EraPatientGroups
                eraClaims={patientClaims}
                eraLogId={era._id}
                readOnly
                allocationMode={era.allocationMode || "auto"}
              />
            ) : (
              <p className="text-center mb-0">
                No claim detail on this ERA for this patient.
              </p>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={onHide}
          style={{ minWidth: 120 }}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
